import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ScormPackageModel } from 'src/models/scormPackageModel';
import { DiffusionPilotageModel } from 'src/models/pilotage/diffusionPilotageModel';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ajouter-modifier-diffusion-multiple-dialog',
    templateUrl: './ajouter-modifier-diffusion-multiple-dialog.component.html',
    styleUrls: ['./ajouter-modifier-diffusion-multiple-dialog.component.scss'],
    imports: [
        MatDialogModule,
        TranslateModule,
        MatProgressSpinnerModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatOptionModule,
        NgxMatSelectSearchModule,
        DragDropModule
    ]
})
export class AjouterModifierDiffusionMultipleDialogComponent implements OnInit, OnDestroy {
  scormPackagesDisponibles: ScormPackageModel[] = [];
  scormPackagesSelectionnes: ScormPackageModel[] = [];
  titre: string = '';
  langueSelectionnee: string | null = null;
  currentLang: string = 'fr';

  scormPackageFilterCtrl = new FormControl();
  filteredScormPackages = new ReplaySubject<ScormPackageModel[]>(1);
  private _onDestroy = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<AjouterModifierDiffusionMultipleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { mode: 'ajouter' | 'modifier'; diffusion?: DiffusionPilotageModel; scormPackages: ScormPackageModel[] },
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang || 'fr';

    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
      this.filterScormPackages();
    });

    const tousLesPackages = this.data.scormPackages || [];

    if (this.data.mode === 'modifier' && this.data.diffusion) {
      const idsSelectionnes = this.data.diffusion.scormPackageIds || [];

      this.scormPackagesSelectionnes = idsSelectionnes
        .map(id => tousLesPackages.find(p => p.scormPackageId === id))
        .filter((p): p is ScormPackageModel => p !== undefined);

      this.scormPackagesDisponibles = tousLesPackages
        .filter(p => !idsSelectionnes.includes(p.scormPackageId))
        .sort((a, b) => this.getPackageTitle(a).localeCompare(this.getPackageTitle(b)));

      this.titre = this.data.diffusion.titre || '';
      this.langueSelectionnee = this.data.diffusion.langue || null;
    } else {
      this.scormPackagesDisponibles = [...tousLesPackages].sort((a, b) =>
        this.getPackageTitle(a).localeCompare(this.getPackageTitle(b))
      );
    }

    this.setupFilterSubscriptions();
    this.filterScormPackages();
  }

  private setupFilterSubscriptions(): void {
    this.scormPackageFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterScormPackages();
      });
  }

  private filterScormPackages(): void {
    if (!this.scormPackagesDisponibles) {
      return;
    }
    let search = this.scormPackageFilterCtrl.value;
    if (!search) {
      this.filteredScormPackages.next(this.scormPackagesDisponibles.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredScormPackages.next(
      this.scormPackagesDisponibles.filter(pkg =>
        this.getPackageTitle(pkg).toLowerCase().indexOf(search) > -1
      )
    );
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  onDeplacerPackage(event: CdkDragDrop<ScormPackageModel[]>): void {
    moveItemInArray(this.scormPackagesSelectionnes, event.previousIndex, event.currentIndex);
  }

  onAjouterPackage(event: any, selectRef: any): void {
    const scormPackageId = Number(event.value);
    if (!scormPackageId) return;

    const index = this.scormPackagesDisponibles.findIndex(p => p.scormPackageId === scormPackageId);
    if (index === -1) return;

    const packageAjouter = this.scormPackagesDisponibles.splice(index, 1)[0];
    this.scormPackagesSelectionnes.push(packageAjouter);

    this.scormPackageFilterCtrl.reset();
    this.filterScormPackages();
  }

  onRetirerPackage(scormPackageId: number): void {
    const index = this.scormPackagesSelectionnes.findIndex(p => p.scormPackageId === scormPackageId);
    if (index === -1) return;

    const packageRetire = this.scormPackagesSelectionnes.splice(index, 1)[0];
    this.scormPackagesDisponibles.push(packageRetire);
    this.scormPackagesDisponibles.sort((a, b) =>
      this.getPackageTitle(a).localeCompare(this.getPackageTitle(b))
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.scormPackagesSelectionnes.length > 0 && this.langueSelectionnee && this.titre.trim()) {
      this.dialogRef.close({
        scormPackageIds: this.scormPackagesSelectionnes.map(p => p.scormPackageId),
        titre: this.titre,
        langue: this.langueSelectionnee
      });
    }
  }

  getPackageTitle(scormPackage: ScormPackageModel): string {
    const locale = scormPackage.locales?.find(l => l.langue === this.currentLang)
      || scormPackage.locales?.find(l => l.langue === 'fr')
      || scormPackage.locales?.[0];
    return locale?.titre || scormPackage.nomPackage || '';
  }
}
